import * as React from 'react';
import { CheckCircleIcon, InformationCircleIcon } from '@heroicons/react/24/solid';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { ExclamationCircleIcon as ExclamationCircleSolidIcon } from '@heroicons/react/24/solid';
import { cn } from '@restworld/utils-common';
export type AlertProps = {
  text: string | React.ReactNode;
  wrapperStyling?: string;
  icon?: React.ReactNode;
  textStyling?: string;
  variant?: 'success' | 'error' | 'info' | 'warning' | 'restworld';
};

export const Alert = ({ wrapperStyling, icon, textStyling, text, variant = 'error' }: AlertProps) => {
  return (
    <AlertWrapper {...{ wrapperStyling, variant }}>
      {icon
        ? icon
        : variant
        ? {
            success: <CheckCircleIcon className="text-green-700 min-w-[18px] min-h-[18px] max-w-[18px] max-h-[18px]" />,
            error: (
              <ExclamationCircleIcon className="relative text-red-600 min-w-[24px] min-h-[24px] max-w-[24px] max-h-[24px]" />
            ),
            info: (
              <ExclamationCircleSolidIcon className="text-yellow-700 rotate-180 min-w-[24px] min-h-[24px] max-w-[24px] max-h-[24px]" />
            ),
            warning: (
              <InformationCircleIcon className="fill-orange-700 min-w-[16px] min-h-[16px] max-w-[16px] max-h-[16px]" />
            ),
            restworld: (
              <InformationCircleIcon className="relative text-purple-600 min-w-[24px] min-h-[24px] max-w-[24px] max-h-[24px]" />
            ),
          }[variant]
        : null}
      <StyledText {...{ textStyling, variant }}>{text}</StyledText>
    </AlertWrapper>
  );
};

export const AlertWrapper = ({
  wrapperStyling,
  variant,
  className,
  children,
  ...props
}: Pick<AlertProps, 'wrapperStyling' | 'variant'> & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-row items-center gap-2 lg:p-3 p-3 rounded-lg border self-stretch',
      variant &&
        {
          success: 'border-green-400 bg-green-50 rounded-md items-center',
          error: 'border-red-600 bg-red-50',
          info: 'bg-yellow-100 border-l-4 border-amber-500',
          warning: 'border-orange-700 bg-orange-50 border rounded-lg',
          restworld: 'border-purple-700 bg-purple-50',
        }[variant],
      wrapperStyling,
      className
    )}
    {...props}
  >
    {children}
  </div>
);

export const StyledText = ({
  textStyling,
  variant,
  className,
  children,
  ...props
}: Pick<AlertProps, 'textStyling' | 'variant'> & React.HTMLAttributes<HTMLParagraphElement>) => (
  <p
    className={cn(
      'lg:text-base lg:leading-5.25 text-sm leading-5.25',
      variant &&
        {
          success: 'text-green-700',
          error: 'text-red-600',
          info: 'text-yellow-800',
          warning: 'text-orange-700',
          restworld: 'text-purple-700 font-medium',
        }[variant],
      textStyling,
      className
    )}
    {...props}
  >
    {children}
  </p>
);
